import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SubjectConditionEditPanelProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'subject_includes_words' | 'subject_matches_patterns';
  value: string[];
  onChange: (value: string[]) => void;
}

export function SubjectConditionEditPanel({ 
  isOpen, 
  onClose, 
  type, 
  value, 
  onChange 
}: SubjectConditionEditPanelProps) {
  const [newItem, setNewItem] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleAddItem = () => {
    if (!newItem.trim()) {
      setError("Please enter a value");
      return;
    }

    if (value.includes(newItem.trim())) {
      setError("This item already exists");
      return;
    }

    onChange([...value, newItem.trim()]);
    setNewItem("");
    setError(null);
  };

  const handleRemoveItem = (index: number) => {
    const itemToEdit = value[index];
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
    setNewItem(itemToEdit);
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddItem();
    }
  };

  const getTitle = () => {
    return type === 'subject_includes_words' 
      ? "Edit Subject Words" 
      : "Edit Subject Patterns";
  };

  const getPlaceholder = () => {
    return type === 'subject_includes_words' 
      ? "Enter a word or phrase" 
      : "Enter a pattern (use * for wildcards)";
  };

  const getHelpText = () => {
    if (type === 'subject_includes_words') {
      return "Add words or phrases that should be found in the email subject. The match is case-insensitive.";
    } else {
      return "Add text patterns to match against email subjects. Use * as a wildcard for one or more characters (e.g., *urgent*, meeting*).";
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>{getTitle()}</SheetTitle>
        </SheetHeader>
        
        <div className="space-y-4 mt-6">
          <p className="text-sm text-muted-foreground">
            {getHelpText()}
          </p>

          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder={getPlaceholder()}
                value={newItem}
                onChange={(e) => {
                  setNewItem(e.target.value);
                  setError(null);
                }}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button onClick={handleAddItem} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {error && (
              <Alert variant="destructive" className="py-2">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">
              Current {type === 'subject_includes_words' ? 'Words' : 'Patterns'}
            </h4>
            
            {value.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No items added yet
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {value.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                  >
                    <span className="text-sm text-foreground">{item}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={onClose}
            className="flex-1"
            disabled={value.length === 0}
          >
            Save
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}